import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { generatedApiClient } from '@/services/generatedApiClient';
import { cacheConfig } from '@/services/queryClient';
import type { ApiStoreHistoryPostInput } from '@/generated/api';

// Query keys for consistent cache management
export const storeKeys = {
  all: ['store'] as const,
  chats: (namespace: string) => [...storeKeys.all, 'chats', namespace] as const,
  chatHistory: (namespace: string, chat: string) =>
    [...storeKeys.all, 'history', namespace, chat] as const,
  chatModelPairs: (namespace: string, chat: string) =>
    [...storeKeys.all, 'models-pairs', namespace, chat] as const,
  chatUniqueModels: (namespace: string, chat: string) =>
    [...storeKeys.all, 'models-unique', namespace, chat] as const,
  tags: () => [...storeKeys.all, 'tags'] as const,
} as const;

/**
 * Hook for getting chats in a namespace with optimized caching
 */
export const useChats = (
  namespace: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: storeKeys.chats(namespace),
    queryFn: async () => {
      const response = await generatedApiClient.getChats(namespace);
      if (response.error) {
        throw new Error(response.error);
      }
      // Exclude image chats (ids starting with 'img_') from text history
      const list = response.data || [];
      return Array.isArray(list)
        ? list.filter(id => typeof id === 'string' && !id.startsWith('img_'))
        : [];
    },
    // Use optimized cache settings for store data
    staleTime: cacheConfig.store.staleTime,
    gcTime: cacheConfig.store.gcTime,
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) errors
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      return failureCount < 2;
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: (options?.enabled ?? true) && !!namespace,
  });
};

/**
 * Hook for getting chat history with optimized caching
 */
export const useChatHistory = (
  namespace: string,
  chat: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: storeKeys.chatHistory(namespace, chat),
    queryFn: async () => {
      const response = await generatedApiClient.getChatHistory(namespace, chat);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    // Chat history changes frequently during active conversations
    staleTime: 30 * 1000, // 30 seconds
    gcTime: cacheConfig.store.gcTime,
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) errors
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      return failureCount < 2;
    },
    // Enable aggressive refetching for active conversations
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: (options?.enabled ?? true) && !!namespace && !!chat,
  });
};

/**
 * Hook for updating chat history
 */
export const useUpdateChatHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      namespace,
      chat,
      historyData,
    }: {
      namespace: string;
      chat: string;
      historyData: ApiStoreHistoryPostInput;
    }) => generatedApiClient.updateChatHistory(namespace, chat, historyData),
    onSuccess: (_, variables) => {
      // Invalidate specific chat history and chat list
      queryClient.invalidateQueries({
        queryKey: storeKeys.chatHistory(variables.namespace, variables.chat),
      });
      queryClient.invalidateQueries({
        queryKey: storeKeys.chats(variables.namespace),
      });
    },
    onError: error => {
      console.error('Chat history update failed:', error);
    },
  });
};

/**
 * Hook for getting chat messageId->model pairs
 */
export const useChatModelPairs = (
  namespace: string,
  chat: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: storeKeys.chatModelPairs(namespace, chat),
    queryFn: async () => {
      const response = await generatedApiClient.getChatModelPairs(
        namespace,
        chat
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || {};
    },
    staleTime: 60 * 1000,
    gcTime: cacheConfig.store.gcTime,
    enabled: (options?.enabled ?? true) && !!namespace && !!chat,
  });
};

/**
 * Hook for getting unique models used in a chat
 */
export const useChatUniqueModels = (
  namespace: string,
  chat: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: storeKeys.chatUniqueModels(namespace, chat),
    queryFn: async () => {
      const response = await generatedApiClient.getChatUniqueModels(
        namespace,
        chat
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
    staleTime: 60 * 1000,
    gcTime: cacheConfig.store.gcTime,
    enabled: (options?.enabled ?? true) && !!namespace && !!chat,
  });
};

/**
 * Hook for global tags list
 */
export const useTags = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: storeKeys.tags(),
    queryFn: async () => {
      const response = await generatedApiClient.getTags();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: cacheConfig.store.gcTime,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook for deleting a chat
 */
export const useDeleteChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ namespace, chat }: { namespace: string; chat: string }) =>
      generatedApiClient.deleteChat(namespace, chat),
    onSuccess: (_, variables) => {
      // Remove the specific chat history from cache
      queryClient.removeQueries({
        queryKey: storeKeys.chatHistory(variables.namespace, variables.chat),
      });
      // Invalidate chat list to reflect the deletion
      queryClient.invalidateQueries({
        queryKey: storeKeys.chats(variables.namespace),
      });
    },
    onError: error => {
      console.error('Chat deletion failed:', error);
    },
  });
};
